import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight } from 'lucide-react';
import Navigation from '../components/home/Navigation';
import Footer from '../components/home/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const firms = [
  {
    name: 'OSG Analytics',
    leader: 'Dr. R. Sukumar',
    role: 'Predictive Intelligence',
    url: 'https://osganalytics.com',
    logo: `${API}/resources/osg-logo.svg`,
    color: '#0B1A2B',
    description: 'OSG Analytics delivers advanced customer experience and behavioral analytics through proprietary PaaS and SaaS products. Their tech-enabled services combine real-time analytics with actionable diagnostics to help businesses turn complex data into strategic advantage.',
    capabilities: [
      'Real-Time Analytics & Actionable Diagnostics',
      'Proprietary ASEMAP Methodology',
      'Customer Experience & Behavioral Analytics',
      'Market Research & Predictive Insights',
      'Tech-Enabled PaaS & SaaS Products',
    ],
    sectors: ['MedTech', 'Retail', 'Pharma & Healthcare', 'Consumer Goods', 'Financial Services', 'Energy', 'Entertainment', 'Government'],
    tagline: 'Turning complex behavioral data into validated, actionable intelligence.',
  },
  {
    name: 'Brackett Partners',
    leader: 'Erania Brackett',
    role: 'Strategic Integration & M&A Advisory',
    url: 'https://brackett.partners',
    logo: `${API}/resources/brackett-favicon.png`,
    color: '#5A3E2B',
    description: 'Brackett Partners provides AI-powered predictive intelligence and customer-validated strategy to drive measurable growth. Their integrated advisory, agency, and M&A capabilities serve venture-backed companies, Fortune-ranked enterprises, and public institutions.',
    capabilities: [
      'Customer-Validated Growth & Go-to-Market Strategy',
      'Predictive Intelligence & AI for Marketing',
      'Brand Positioning & Narrative Development',
      'Digital CMO Leadership & Marketing Execution',
      'Sell-Side M&A Advisory (with Britehorn Securities)',
    ],
    sectors: ['Healthcare', 'Industrial', 'Consumer', 'Technology', 'Public Sector'],
    tagline: 'Customer-validated strategy from positioning through close.',
  },
  {
    name: 'Harper Cook Consulting',
    leader: 'Forest T. Harper Jr.',
    role: 'Growth Activation & Leadership Alignment',
    url: 'https://harpercookconsultingllc.com',
    logo: `${API}/resources/harpercook-logo.png`,
    color: '#6B2020',
    description: 'Harper Cook Consulting partners with leaders to accelerate growth and create lasting impact through strategic leadership, organizational transformation, and capital strategy. They specialize in clarifying direction, building internal capacity, and aligning executive teams for sustainable outcomes.',
    capabilities: [
      'Strategic Leadership for Sustainable Impact',
      'Organizational Transformation & Capacity Building',
      'Capital Strategy & Collaborative Growth',
      'Executive Team Alignment & Priority Clarification',
      'Fundraising Strategy & Capital Campaigns',
    ],
    sectors: ['Non-Profit', 'Philanthropic', 'Corporate', 'Government', 'Social Impact'],
    tagline: 'Accelerating growth through leadership alignment and disciplined execution.',
  },
];

export default function PartnerFirmsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="partner-firms-page">
      <Helmet>
        <title>Partner Firms — OSG Analytics, Brackett Partners, Harper Cook | APIG Alliance</title>
        <meta name="description" content="Three firms. One integrated capability. OSG Analytics (Predictive Intelligence), Brackett Partners (Strategic Positioning & M&A), and Harper Cook Consulting (Growth Activation & Leadership Alignment) form the APIG Alliance." />
        <link rel="canonical" href="https://predictiveintelligencegrowthalliance.com/partners" />
      </Helmet>
      <Navigation />

      {/* Header */}
      <section className="pt-32 pb-16 bg-white">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="overline mb-4">The Alliance</p>
            <h1 className="heading-display text-4xl sm:text-5xl text-[#0B1A2B] mb-6" data-testid="firms-headline">
              Three Firms. One Integrated Capability.
            </h1>
            <p className="body-large">
              Each APIG partner brings a distinct discipline — predictive intelligence, strategic positioning, and growth activation — operating independently and converging when the challenge demands it.
            </p>
          </div>
        </div>
      </section>

      {/* Firm Cards */}
      <section className="pb-24 bg-white">
        <div className="container-luxury">
          <div className="space-y-16 lg:space-y-24">
            {firms.map((firm, index) => (
              <FirmCard key={firm.name} firm={firm} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Logo Lockup */}
      <section className="py-16 bg-white">
        <div className="container-luxury text-center">
          <img
            src={`${API}/resources/apig-logo-lockup.png`}
            alt="APIG Alliance — OSG | Brackett | Harper & Cook"
            className="mx-auto h-24 sm:h-28 lg:h-32 object-contain"
            data-testid="partner-logo-lockup"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1A2B] py-20">
        <div className="container-luxury text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">
            Bring us the challenge before you place the bet.
          </h2>
          <p className="text-[#94A3B8] text-base mb-8 max-w-2xl mx-auto">
            Whether your challenge calls for one capability or all three, the Alliance configures around what you need.
          </p>
          <a
            href="/#contact"
            className="inline-block bg-[#D97706] text-white px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#B45309] transition-colors"
            data-testid="firms-cta"
          >
            Submit Your Challenge
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FirmCard({ firm, index }) {
  const isEven = index % 2 === 0;

  return (
    <div
      className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-start ${isEven ? '' : 'lg:direction-rtl'}`}
      data-testid={`firm-card-${index}`}
    >
      {/* Info Column */}
      <div className={`${isEven ? '' : 'lg:order-2'}`}>
        {/* Leader badge */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-white border border-[#E2E8F0] flex items-center justify-center p-1.5">
            <img
              src={firm.logo}
              alt={`${firm.name} logo`}
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-[#D97706] uppercase tracking-wider">{firm.role}</p>
            <p className="text-xs text-[#64748B]">{firm.leader}</p>
          </div>
        </div>

        <h2 className="font-serif text-3xl sm:text-4xl text-[#0B1A2B] mb-4" data-testid={`firm-name-${index}`}>
          {firm.name}
        </h2>

        <p className="text-[#475569] leading-relaxed mb-6">
          {firm.description}
        </p>

        <p className="font-serif text-lg italic text-[#64748B] mb-8 border-l-2 border-[#D97706] pl-4">
          {firm.tagline}
        </p>

        <a
          href={firm.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#0B1A2B] text-white px-6 py-3 text-sm font-medium tracking-wide hover:bg-[#1E293B] transition-colors"
          data-testid={`firm-link-${index}`}
        >
          Visit {firm.name}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>

      {/* Capabilities Column */}
      <div className={`${isEven ? '' : 'lg:order-1'}`}>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-8 lg:p-10">
          <h3 className="text-xs font-medium uppercase tracking-wider text-[#D97706] mb-6">
            Key Capabilities
          </h3>
          <ul className="space-y-4 mb-8">
            {firm.capabilities.map((cap, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D97706] mt-2 flex-shrink-0" />
                <span className="text-[#334155] text-[0.9375rem] leading-relaxed">{cap}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-xs font-medium uppercase tracking-wider text-[#D97706] mb-4">
            Sectors Served
          </h3>
          <div className="flex flex-wrap gap-2">
            {firm.sectors.map((sector) => (
              <span
                key={sector}
                className="bg-[#0B1A2B] text-white text-xs px-3 py-1.5 font-medium"
              >
                {sector}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
